import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ChoiceButton } from "@/components/ChoiceButton";
import { toast } from "sonner";
import { Ruler, Check, Pencil, X } from "lucide-react";

// MEASUREMENTS THE AI READ (his plan, 3 Nov) — one at a time: Keep, Correct or Remove. No "keep all". A person's value
// and the tag's printed value are never replaced by an AI estimate; the server says so if one is already there.
const UNITS = { weight: ["lbs", "kg", "oz", "g"], spline_count: ["teeth"] };
const LENGTH = ["in", "mm", "cm", "ft"];
const words = (t) => String(t || "").replace(/_/g, " ");

function Row({ m, partId, rid, onDone }) {
  const [fixing, setFixing] = useState(false);
  const [val, setVal] = useState("");
  const [unit, setUnit] = useState(m.unit);
  const [busy, setBusy] = useState(false);
  const decide = async (decision, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${partId}/ai-research/${rid}/measurements/${m.index}`, { decision, ...extra });
      toast.success(data.message);
      onDone(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save that."); }
    setBusy(false);
  };
  const t = `ai-measure-${m.index}`;
  if (m.decided) {
    return (
      <div data-testid={`${t}-decided`} className="flex items-center gap-2 text-xs text-slate-500 px-2 py-1.5">
        <Check className="w-3.5 h-3.5 text-emerald-600" />{words(m.type)} {m.note ? `(${m.note})` : ""} — {m.decided.outcome}
        {m.decided.value != null && <span className="font-mono">· saved {m.decided.value} {m.decided.unit}</span>}
      </div>
    );
  }
  return (
    <div data-testid={t} className="rounded-lg border border-violet-200 bg-violet-50/40 px-2.5 py-2 space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-2 text-sm">
        <span className="font-bold capitalize">{words(m.type)}</span>
        {m.note && <span className="text-xs text-slate-500">{m.note}</span>}
        <span data-testid={`${t}-value`} className="font-mono font-bold">{m.value} {m.unit}</span>
        <span className="text-[10px] uppercase tracking-wider text-violet-700">{m.source === "tag" ? "printed on the tag" : "read off the photo"} · {m.confidence}</span>
      </div>
      {m.on_part && !m.on_part.same && (
        <div data-testid={`${t}-on-part`} className="text-[11px] text-amber-800">
          On the part now: {m.on_part.value} {m.on_part.unit} ({m.on_part.who}){m.can_keep ? "" : " — it stays; Keep is off"}
        </div>
      )}
      {fixing ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <Input data-testid={`${t}-fix-value`} inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Right value" className="h-10 w-28" />
          <select data-testid={`${t}-fix-unit`} value={unit} onChange={(e) => setUnit(e.target.value)} className="h-10 rounded-lg border border-slate-300 px-2 text-sm">
            {(UNITS[m.type] || LENGTH).map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <ChoiceButton data-testid={`${t}-fix-save`} tone="sky" disabled={busy || !val.trim()} onClick={() => decide("corrected", { value: Number(val), unit })}>Save my value</ChoiceButton>
          <ChoiceButton data-testid={`${t}-fix-cancel`} onClick={() => setFixing(false)}>Back</ChoiceButton>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          <ChoiceButton data-testid={`${t}-keep`} tone="sky" disabled={busy || !m.can_keep} onClick={() => decide("kept")}><Check className="w-3.5 h-3.5 mr-1" />Keep</ChoiceButton>
          <ChoiceButton data-testid={`${t}-correct`} disabled={busy} onClick={() => setFixing(true)}><Pencil className="w-3.5 h-3.5 mr-1" />Correct</ChoiceButton>
          <ChoiceButton data-testid={`${t}-remove`} tone="danger" disabled={busy} onClick={() => decide("removed")}><X className="w-3.5 h-3.5 mr-1" />Remove</ChoiceButton>
        </div>
      )}
    </div>
  );
}

export function AiMeasurementsPanel({ partId, returnId, onSaved }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    if (!returnId) return;
    api.get(`/parts/${partId}/ai-research/${returnId}/measurements`).then(({ data }) => setRows(data.rows || [])).catch(() => setRows([]));
  }, [partId, returnId]);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  const done = (data) => { setRows(data.rows || []); onSaved?.(); };
  return (
    <div data-testid="ai-measurements" className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        <Ruler className="w-3.5 h-3.5" />Measurements the AI read — keep, correct or remove each one
      </div>
      {rows.map((m) => <Row key={m.index} m={m} partId={partId} rid={returnId} onDone={done} />)}
    </div>
  );
}
